import { z } from "zod";
import { BODY_MAX, CHANNELS, PUBLIC_AUDIENCE, TITLE_MAX, displayStatus, normalizeChannels, type Announcement, type AnnouncementChannel } from "./model";
import { EXPIRY_OPTIONS, expiryFor, isoToZoned, zonedToIso, type ExpiryOption } from "./schedule";
import type { SaveRequest } from "./use-announcements";

export const ALL_AGENTS = "all";

/** Messages are i18n keys under `engage.announcements.composer`. */
export const composerSchema = z.object({
  title: z.string().trim().min(1, "titleRequired").max(TITLE_MAX),
  body: z.string().trim().min(1, "messageRequired").max(BODY_MAX),
  urgent: z.boolean(),
  audiences: z.array(z.number().int()).min(1, "audienceRequired"),
  segments: z.array(z.string()),
  channels: z.array(z.enum(CHANNELS as [AnnouncementChannel, ...AnnouncementChannel[]])),
  when: z.enum(["now", "later"]),
  date: z.string(),
  time: z.string(),
  teach: z.boolean(),
  expiry: z.enum(EXPIRY_OPTIONS),
  agent: z.string(),
});

export type ComposerValues = z.infer<typeof composerSchema>;
export type SaveIntent = "send" | "draft";

const DEFAULT_CHANNELS: AnnouncementChannel[] = ["push", "webapp_banner", "widget_pin"];
const DEFAULT_TIME = "08:00";
const DAY_MS = 86_400_000;

/** Empty composer (mockup defaults: everyone, push + web app + widget, teach for 2 days). */
export function emptyValues(timeZone: string, now = Date.now()): ComposerValues {
  const tomorrow = isoToZoned(new Date(now + DAY_MS).toISOString(), timeZone);
  return {
    title: "",
    body: "",
    urgent: false,
    audiences: [PUBLIC_AUDIENCE],
    segments: [],
    channels: DEFAULT_CHANNELS,
    when: "now",
    date: tomorrow.date,
    time: DEFAULT_TIME,
    teach: true,
    expiry: "2d",
    agent: ALL_AGENTS,
  };
}

export function valuesFrom(item: Announcement, timeZone: string): ComposerValues {
  const base = emptyValues(timeZone);
  const scheduled = item.scheduled_at ? isoToZoned(item.scheduled_at, timeZone) : null;
  const expiry: ExpiryOption = item.teach.enabled && !item.teach.expires_at ? "never" : "2d";
  return {
    ...base,
    title: item.title,
    body: item.body,
    urgent: item.urgent,
    audiences: item.audiences.length ? item.audiences : base.audiences,
    segments: item.segments,
    channels: normalizeChannels(item.channels),
    when: displayStatus(item.status) === "scheduled" ? "later" : "now",
    date: scheduled?.date ?? base.date,
    time: scheduled?.time ?? base.time,
    teach: item.teach.enabled,
    expiry,
    agent: item.teach.agent_ids.length === 1 ? (item.teach.agent_ids[0] ?? ALL_AGENTS) : ALL_AGENTS,
  };
}

/** Scheduled send instant (ISO) or null for "send now"; `undefined` when the schedule is invalid/past. */
export function sendAtOf(values: ComposerValues, timeZone: string, now = Date.now()): string | null | undefined {
  if (values.when === "now") return null;
  const iso = zonedToIso(values.date, values.time, timeZone);
  if (!iso || Date.parse(iso) <= now) return undefined;
  return iso;
}

interface RequestContext {
  intent: SaveIntent;
  sendAt: string | null;
  timeZone: string;
  editing: Announcement | null;
  now?: number;
}

/** Builds the API call for the composer's Save draft / Send / Schedule buttons. */
export function toSaveRequest(values: ComposerValues, ctx: RequestContext): SaveRequest {
  const sendAt = ctx.intent === "draft" ? null : ctx.sendAt;
  const teach = {
    enabled: values.teach,
    expires_at: values.teach ? expiryFor(values.expiry, sendAt ? Date.parse(sendAt) : (ctx.now ?? Date.now()), ctx.timeZone) : null,
    agent_ids: values.agent === ALL_AGENTS ? [] : [values.agent],
  };
  const fields = {
    title: values.title.trim(),
    body: values.body.trim(),
    urgent: values.urgent,
    audiences: values.audiences,
    segments: values.segments,
    channels: values.channels,
    teach,
  };
  if (!ctx.editing) return { kind: "create", body: { ...fields, send_at: sendAt, draft: ctx.intent === "draft" } };
  const wasScheduled = displayStatus(ctx.editing.status) === "scheduled";
  return {
    kind: "update",
    id: ctx.editing.id,
    body: { ...fields, send_at: sendAt, unschedule: ctx.intent === "draft" && wasScheduled },
    sendNow: ctx.intent === "send" && sendAt === null,
  };
}
